import _ from 'lodash';
import {Record, Map, List} from 'immutable';
import {weakMemo} from '@packages/utilities/memo';
import {getUploadData} from '@packages/utilities/file-upload';
import {reportBug} from '@packages/utilities/reporter';
import SharedConfig from '@packages/systems/core/constants/SharedConfig';
import {
  FileRecord,
  type FileRecordType,
} from '@packages/systems/core/records/FileRecord';
import {
  ASSET_S3_UPLOAD_NOT_NEEDED,
  ASSET_S3_UPLOAD_NEEDED,
} from '@packages/systems/designer/actions';
import type {StyleSheetImageResolverInterface} from './styleSheetAssetUrls';

const sharedPaths = SharedConfig.paths;

export type AssetsType = Map<string, FileRecordType>;
type AssetStateProps = {
  assets: AssetsType;
  s3PingQueue: List<
    | string
    | {
        input: string;
        actions: {
          success: typeof ASSET_S3_UPLOAD_NOT_NEEDED;
          error: typeof ASSET_S3_UPLOAD_NEEDED;
        };
        meta: {
          mintAsset: FileRecordType;
          signingResponse: Map<string, string>;
        };
      }
  >;
};

const defaultValues: AssetStateProps = {
  assets: Map({
    // Include default placeholder inline image
    example_img: new FileRecord({
      _id: sharedPaths.EXAMPLE_IMG_ID,
      cdnUrl: sharedPaths.EXAMPLE_IMG_URL,
      fileName: sharedPaths.EXAMPLE_IMG_NAME,
      origFileName: sharedPaths.EXAMPLE_IMG_NAME,
      width: 150,
      height: 150,
      fileSize: 2063,
      placeholder: true,
      alt: '',
    }),

    // Include default placeholder bg image
    example_bg: new FileRecord({
      _id: sharedPaths.EXAMPLE_BG_ID,
      cdnUrl: sharedPaths.EXAMPLE_BG_URL,
      fileName: sharedPaths.EXAMPLE_BG_NAME,
      origFileName: sharedPaths.EXAMPLE_BG_NAME,
      width: 250,
      height: 250,
      fileSize: 3618,
      placeholder: true,
      alt: '',
    }),

    // Include default lightbox thumbnail placeholder image
    example_thumb: new FileRecord({
      _id: sharedPaths.EXAMPLE_THUMB_ID,
      cdnUrl: sharedPaths.EXAMPLE_THUMB_URL,
      fileName: sharedPaths.EXAMPLE_THUMB_NAME,
      origFileName: sharedPaths.EXAMPLE_THUMB_NAME,
      width: 150,
      height: 150,
      fileSize: 3374,
      placeholder: true,
      alt: '',
    }),

    example_prebuilts_bg: new FileRecord({
      _id: sharedPaths.EXAMPLE_PREBUILTS_V2_BG_ID,
      cdnUrl: sharedPaths.EXAMPLE_PREBUILTS_V2_BG_URL,
      fileName: sharedPaths.EXAMPLE_PREBUILTS_V2_BG_NAME,
      origFileName: sharedPaths.EXAMPLE_PREBUILTS_V2_BG_NAME,
      width: 1305,
      height: 833,
      fileSize: 3910,
      placeholder: true,
      alt: '',
    }),

    // Include default page not found icon
    page_not_found: new FileRecord({
      _id: sharedPaths.PAGE_NOT_FOUND_ICON_ID,
      cdnUrl: sharedPaths.PAGE_NOT_FOUND_ICON_URL,
      fileName: sharedPaths.PAGE_NOT_FOUND_ICON_NAME,
      origFileName: sharedPaths.PAGE_NOT_FOUND_ICON_NAME,
      width: 47,
      height: 59,
      fileSize: 754,
      placeholder: true,
      alt: '',
    }),

    // Include default password page icon
    password_page_icon: new FileRecord({
      _id: sharedPaths.PASSWORD_PAGE_ICON_ID,
      cdnUrl: sharedPaths.PASSWORD_PAGE_ICON_URL,
      fileName: sharedPaths.PASSWORD_PAGE_ICON_NAME,
      origFileName: sharedPaths.PASSWORD_PAGE_ICON_NAME,
      width: 48,
      height: 68,
      fileSize: 1113,
      placeholder: true,
      alt: '',
    }),

    // Include default youtube placeholder image
    youtube_placeholder: new FileRecord({
      _id: sharedPaths.YOUTUBE_PLACEHOLDER_ID,
      cdnUrl: sharedPaths.YOUTUBE_PLACEHOLDER_URL,
      fileName: sharedPaths.YOUTUBE_PLACEHOLDER_NAME,
      origFileName: sharedPaths.YOUTUBE_PLACEHOLDER_NAME,
      width: 560,
      height: 315,
      fileSize: 1284,
      placeholder: true,
      alt: '',
    }),

    // Include check for custom checkbox
    custom_checkbox_check: new FileRecord({
      _id: sharedPaths.CUSTOM_CHECKBOX_CHECK_ID,
      cdnUrl: sharedPaths.CUSTOM_CHECKBOX_CHECK_URL,
      fileName: sharedPaths.CUSTOM_CHECKBOX_CHECK_NAME,
      origFileName: sharedPaths.CUSTOM_CHECKBOX_CHECK_NAME,
      width: 12,
      height: 12,
      fileSize: 204,
      placeholder: true,
      alt: '',
    }),
  }),
  s3PingQueue: List(),
};

const __HACK__PREBUILT_LAYOUTS_ASSET_URLS = {
  'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc421496616bf357c25_placeholder.svg':
    'example_img',
  'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc4f8e818ea77d7c5fa_placeholder-thumb.svg':
    'example_thumb',
} as const;

export const AssetStateBase = Record(defaultValues);

// Implementing StyleSheetImageResolverInterface lets the full store state be
// passed anywhere, interchangeably with the canvas-optimized
// styleSheetImageResolver.
export class AssetState
  extends AssetStateBase
  implements StyleSheetImageResolverInterface
{
  declare assets: AssetStateProps['assets'];
  declare s3PingQueue: AssetStateProps['s3PingQueue'];

  getByUrl(url: string): FileRecordType | null {
    if (!url) {
      return null;
    }

    // HACK: Our prebuilt layouts do not have `img.id` set, and use a different
    // CDN URL for the placeholder SVG than what we use by default, so we need
    // to force it to use our `example_img` when we encounter this URL, at least
    // until we run a migration that replaces all of these `attr.src`s with the
    // correct `img.id`.
    //
    // See also: https://github.com/webflow/webflow/issues/31085
    // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc421496616bf357c25_placeholder.svg': "example_img"; readonly 'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc4f8e818ea77d7c5fa_placeholder-thumb.svg': "example_thumb"; }'.
    if (__HACK__PREBUILT_LAYOUTS_ASSET_URLS[url]) {
      // @ts-expect-error - TS7053 - Element implicitly has an 'any' type because expression of type 'string' can't be used to index type '{ readonly 'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc421496616bf357c25_placeholder.svg': "example_img"; readonly 'https://uploads-ssl.webflow.com/5db1c76aadcfe25e881680fa/5db86dc4f8e818ea77d7c5fa_placeholder-thumb.svg': "example_thumb"; }'.
      return this.assets.get(__HACK__PREBUILT_LAYOUTS_ASSET_URLS[url]) || null;
    }

    // Fix up URLs based on the legacy tutorial template
    const oldAssCdnRoot = 'http://hw-cdn-prod-assets.webflow.com/';
    if (_.startsWith(url, oldAssCdnRoot)) {
      url = url.replace(oldAssCdnRoot, sharedPaths.UPLOADS_CDN_ROOT);
      // Please see https://github.com/webflow/webflow/pull/39900
      // eslint-disable-next-line webflow/better-bug-reporting
      reportBug('OldCdnRootError', 'During asset lookup', {url});
    }

    const path = extractPathIncludingQueryString(url);
    // @ts-expect-error - TS2345 - Argument of type '(asset: FileRecord | undefined) => boolean | undefined' is not assignable to parameter of type '(value?: FileRecord | undefined, key?: string | undefined, iter?: Iterable<string, FileRecord> | undefined) => boolean'.
    const imageInfo = this.assets.find((asset) => {
      const cdnUrl = asset.get('cdnUrl');
      if (cdnUrl) {
        const assetPath = extractPathIncludingQueryString(cdnUrl);
        return assetPath === path;
      }
    });
    if (!imageInfo) {
      return null;
    }

    return imageInfo;
  }

  getById(id: string): FileRecordType | null {
    if (!id) {
      return null;
    }

    // @ts-expect-error - TS2345 - Argument of type 'null' is not assignable to parameter of type 'FileRecord | undefined'.
    return this.assets.get(id, null);
  }

  getByGuid(guid: string): FileRecordType | null {
    const assetsByGuid = memoized_getAssetsByGuid(this.assets);
    const imageInfo = assetsByGuid[guid];

    if (!imageInfo) {
      return null;
    }

    return imageInfo;
  }

  addImage(asset: FileRecordType): AssetState {
    return this.setIn(['assets', asset._id], asset);
  }

  updateDimensions(imageId: string, width: number, height: number): AssetState {
    return this.mergeIn(['assets', imageId], {width, height});
  }

  getSize(imageId: string): number {
    // @ts-expect-error - TS2339 - Property 'getIn' does not exist on type 'AssetState'.
    return this.getIn(['assets', imageId, 'fileSize'], 0);
  }

  getProcessing(): AssetsType {
    return this.assets.filter((asset) => {
      return asset.processing === true;
    });
  }
}

const memoized_getAssetsByGuid = weakMemo(getAssetsByGuid);

function getAssetsByGuid(assets: AssetsType): Readonly<{
  [key: string]: FileRecordType;
}> {
  const assetsByGuid: {
    [key: string]: FileRecordType;
  } = {};

  for (const asset of assets.values()) {
    const url = asset.get('cdnUrl');
    const {guid} = getUploadData(url);

    // we don't overwrite an asset with a guid
    // with later assets with the same guid,
    // because we want to replicate the behavior
    // of assets.find returning first asset with
    // the matching guid
    if (!assetsByGuid[guid]) {
      assetsByGuid[guid] = asset;
    }
  }

  return assetsByGuid;
}

const COLON = 0x3a;
const SLASH = 0x2f;
const EMPTY_STRING = '';

/**
 * NOTE:
 * This would be simpler to use  URL object parsing, but that is really slow
 * due to all the URL object creation.
 *
 * IMPORTANT:
 * Also, this is not a "real" url parsing algorithm. This function exists
 * to attempt to quickly extract the "pathname" aspect of a well-formed url.
 */
export function extractPathIncludingQueryString(url: string): string {
  const len = url.length;
  // Bailout early if there isn't anything to look at.
  if (len === 0) return EMPTY_STRING;

  let ptr = 0;
  let parsing_protocol = false;
  do {
    let ch = url.charCodeAt(ptr);

    // We hit a '/' character. Check if we have seen a protocol yet.
    // If we haven't then this url doesn't have a protocol.
    if (ch === SLASH && !parsing_protocol) {
      ptr++;
      break;
    }

    if (ch === COLON) {
      if (!parsing_protocol) {
        parsing_protocol = true;
        // Check for rest of protocol separator consuming along the way.
        // If we hit a character that isn't part of the protocol, then
        // we continue to the next iteration bailing on protocol parsing.
        ptr++;
        ch = url.charCodeAt(ptr);
        if (ch !== SLASH) {
          parsing_protocol = false;
          continue;
        }
        ptr++;
        ch = url.charCodeAt(ptr);
        if (ch !== SLASH) {
          parsing_protocol = false;
          continue;
        }
        // Finished parsing protocol. Continue with next character.
        parsing_protocol = false;
        ptr++;
        continue;
      }
    }

    // Advance to the next character on next iteration.
    ptr++;
  } while (ptr < len);

  // Check if there is anything left in the url to be the pathname.
  if (ptr < len - 1) {
    return url.slice(ptr);
  } else {
    return EMPTY_STRING;
  }
}
